import React from "react";
import {
    Button,
    Grid,
    FormControlLabel,
    Checkbox,
    Box,
    Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { BaseDrawer } from "components/Shared/Base/BaseDrawer";
import LoadingWrapper from "components/Shared/LoadingWrapper";
import InnerShadowBox from "components/Shared/InnerShadowBox";
import { BASE_CONSTANTS } from "constants/baseConstants";
import BaseTextField from "components/Shared/Base/BaseTextField";
import { useAuth } from "hooks";

const DocumentationFeeForm = ({
    isBusy = false,
    documentationFeeData = null,
    onSubmit = () => { },
    onCancel = () => { },
    entityId = null,
}) => {
    const { viewEntity } = useAuth();

    const isEditMode = !!documentationFeeData;

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            documentationFee: "",
            isActive: false,
        },
    });

    const isActive = watch("isActive");

    // Reset on edit / new
    React.useEffect(() => {
        if (documentationFeeData) {
            reset({
                documentationFee: documentationFeeData.documentationFee?.toString() || "",
                isActive: documentationFeeData.isActive ?? true,
            });
        } else {
            reset({
                documentationFee: "",
                isActive: true,
            });
        }
    }, [documentationFeeData, reset]);

    const handleOnSubmit = (data) => {
        const payload = {
            entityId: entityId || viewEntity?.entity?._id,
            documentationFee: Number(data.documentationFee),
            isActive: Boolean(data.isActive),
        };
        onSubmit(payload);
    };

    const handleCancel = () => {
        reset();
        onCancel();
    };

    return (
        <LoadingWrapper loading={isBusy}>
            <InnerShadowBox>
                <BaseDrawer.Content>
                    <Grid container spacing={BASE_CONSTANTS.SPACING_LG}>
                        {/* Active Checkbox */}
                        <Grid item xs={12}>
                            <Controller
                                name="isActive"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...field}
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography fontWeight={500}>Active Documentation Fee</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {isEditMode ? (
                                                        isActive
                                                            ? "This documentation fee is active."
                                                            : "This documentation fee is inactive."
                                                    ) : (
                                                        "This will be set as active by default."
                                                    )}
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ alignItems: "flex-start", mt: 1 }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Documentation Fee Amount Input */}
                        <Grid item xs={12}>
                            <Controller
                                name="documentationFee"
                                control={control}
                                rules={{
                                    required: "Documentation fee amount is required",
                                    min: { value: 0, message: "Amount cannot be negative" },
                                }}
                                render={({ field }) => (
                                    <BaseTextField
                                        {...field}
                                        required
                                        fullWidth
                                        label="Documentation Fee (Amount)"
                                        type="number"
                                        inputProps={{
                                            min: 0,
                                            step: "0.01",
                                        }}
                                        error={!!errors.documentationFee}
                                        helperText={
                                            errors.documentationFee?.message ||
                                            "Enter the flat documentation fee amount (not a percentage)."
                                        }
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </BaseDrawer.Content>
            </InnerShadowBox>

            {/* Action Buttons */}
            <BaseDrawer.Actions>
                <Button onClick={handleCancel} disabled={isBusy}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit(handleOnSubmit)}
                    disabled={isBusy}
                >
                    {isEditMode ? "Update" : "Save"} Fee
                </Button>
            </BaseDrawer.Actions>
        </LoadingWrapper>
    );
};

export default DocumentationFeeForm;
